import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  CircleUserRound,
  Clock,
  Package,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Store,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { Panel } from "@/components/game/Panel";
import { MicroLabel, PageHeader, ScreenContainer } from "@/components/game/ScreenKit";
import { supabase } from "@/integrations/supabase/client";
import {
  generateOperatorNames,
  applyOperatorName,
  getOperatorProfile,
} from "@/lib/account/operator-name.functions";
import { archiveOwnAccount } from "@/lib/account/archive.functions";
import { useTutorial } from "@/components/game/TutorialProvider";
import { tServerError } from "@/lib/i18n/server-error";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/game/operator")({
  component: OperatorPage,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

/**
 * Eine Kennzahl auf dunkler Ink-Fläche — wie im Kit (`ProgressScreens.jsx`,
 * OperatorScreen). Bewusst KEIN eigenes Panel: Diese beiden Werte gehören zur
 * Identitätskarte und dürfen ihr optisch nicht davonlaufen.
 */
function StatTile({ icon, label, value }: { icon: ReactNode; label: string; value: number }) {
  return (
    <div className="flex min-h-11 items-center gap-2.5 rounded-md bg-ink px-3 py-2">
      <span className="shrink-0 text-accent">{icon}</span>
      <div className="min-w-0">
        <MicroLabel className="[overflow-wrap:anywhere]">{label}</MicroLabel>
        <div className="font-display text-xl leading-tight tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function OperatorPage() {
  useTutorial("operator_intro");
  const { t } = useTranslation("game");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fetchProfile = useServerFn(getOperatorProfile);
  const generateFn = useServerFn(generateOperatorNames);
  const applyFn = useServerFn(applyOperatorName);
  const archiveFn = useServerFn(archiveOwnAccount);

  const profileQ = useQuery({
    queryKey: ["operator-profile"],
    queryFn: () => fetchProfile(),
  });

  const [picks, setPicks] = useState<{ names: string[]; pick_token: string } | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);

  const genMut = useMutation({
    mutationFn: () => generateFn(),
    onSuccess: (data) => {
      setPicks({ names: data.names, pick_token: data.pick_token });
      setChosen(null);
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const applyMut = useMutation({
    mutationFn: () => applyFn({ data: { pick_token: picks!.pick_token, chosen_name: chosen! } }),
    onSuccess: () => {
      toast.success(t("operator.toast.nameChanged"));
      setPicks(null);
      setChosen(null);
      qc.invalidateQueries({ queryKey: ["operator-profile"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["marketplace"] });
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const profile = profileQ.data;
  const cooldownActive = profile?.cooldown_active ?? false;
  const autoGenerated = profile?.auto_generated ?? false;

  return (
    <ScreenContainer className="max-w-[760px]">
      <Button asChild variant="ghost" size="sm" className="-ml-3 mb-1 min-h-11 sm:min-h-9">
        <Link to="/game/battle">
          <ArrowLeft strokeWidth={2.5} />
          {t("operator.back")}
        </Link>
      </Button>

      <PageHeader
        title="Operator"
        icon={<CircleUserRound className="h-6 w-6 shrink-0" strokeWidth={2.5} />}
        sub={t("operator.subtitle")}
      />

      {/* Identität */}
      <Panel tone="primary" grid contentClassName="p-3.5">
        <MicroLabel>{t("operator.currentOperator")}</MicroLabel>
        <h3 className="mt-1 text-2xl leading-tight [overflow-wrap:anywhere]">
          {profile?.display_name ?? "—"}
        </h3>
        <div className="mt-2 grid gap-1 text-xs text-muted-foreground">
          <div>
            {t("operator.loginEmail")}{" "}
            <span className="font-mono text-foreground [overflow-wrap:anywhere]">
              {profile?.email ?? "—"}
            </span>
          </div>
          <div>
            {t("operator.memberSince")}{" "}
            <span className="tabular-nums text-foreground">
              {formatDate(profile?.member_since)}
            </span>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <StatTile
            icon={<Store className="h-4 w-4" strokeWidth={2.5} />}
            label={t("operator.activeListings")}
            value={profile?.stats.active_listings ?? 0}
          />
          <StatTile
            icon={<Package className="h-4 w-4" strokeWidth={2.5} />}
            label={t("operator.soldTotal")}
            value={profile?.stats.sold_total ?? 0}
          />
        </div>
      </Panel>

      {/* Anzeigenamen ändern */}
      <Panel className="mt-3.5" contentClassName="p-3.5">
        <h3 className="flex items-center gap-2 text-[18px] leading-tight">
          <Sparkles className="h-4 w-4 shrink-0 text-accent" strokeWidth={2.5} />
          {t("operator.renameTitle")}
        </h3>
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          {t("operator.renameHintPre")}{" "}
          <span className="font-extrabold text-foreground">{t("operator.renameHintBold")}</span>{" "}
          {t("operator.renameHintPost")}
        </p>

        {autoGenerated && (
          <div className="mt-3 flex items-start gap-2 rounded-md border-2 border-accent bg-accent/20 px-3 py-2 text-xs">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={2.5} />
            <div>
              {t("operator.autoGeneratedNotePre")}{" "}
              <span className="font-extrabold">{t("operator.autoGeneratedNoteBold")}</span>{" "}
              {t("operator.autoGeneratedNotePost")}
            </div>
          </div>
        )}

        {cooldownActive && !autoGenerated && (
          <div className="mt-3 flex items-start gap-2 rounded-md border-2 border-ink bg-secondary px-3 py-2 text-xs text-muted-foreground">
            <Clock className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.5} />
            <div>
              {t("operator.cooldownNotePre")}{" "}
              <span className="font-extrabold tabular-nums text-foreground">
                {formatDate(profile?.next_change_allowed_at)}
              </span>
              .
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => genMut.mutate()}
            disabled={cooldownActive || genMut.isPending || applyMut.isPending}
            className="w-full sm:w-auto"
          >
            <RefreshCw className={genMut.isPending ? "animate-spin" : ""} strokeWidth={2.5} />
            {picks ? t("operator.btnRefreshNames") : t("operator.btnGenerateNames")}
          </Button>
        </div>

        {picks && (
          <div className="mt-3.5">
            <MicroLabel className="mb-1.5">{t("operator.chooseNameLabel")}</MicroLabel>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {picks.names.map((n) => {
                const selected = chosen === n;
                return (
                  <motion.button
                    key={n}
                    type="button"
                    onClick={() => setChosen(n)}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      "flex min-h-11 items-center justify-between gap-2 rounded-md border-2 px-3 py-2",
                      "text-left font-display text-[15px] leading-tight [overflow-wrap:anywhere]",
                      "shadow-hard-sm transition-[transform,box-shadow,filter,background-color] duration-100",
                      "hover:-translate-y-px hover:brightness-[1.06] active:translate-y-[4px] active:shadow-none",
                      selected
                        ? "border-primary bg-primary/20"
                        : "border-ink bg-card hover:border-primary/60",
                    )}
                  >
                    <span>{n}</span>
                    {selected && (
                      <Check className="h-4 w-4 shrink-0 text-primary" strokeWidth={2.5} />
                    )}
                  </motion.button>
                );
              })}
            </div>

            <Button
              onClick={() => applyMut.mutate()}
              disabled={!chosen || applyMut.isPending}
              className="mt-3 w-full sm:w-auto"
            >
              {applyMut.isPending
                ? t("operator.btnApplyPending")
                : chosen
                  ? t("operator.btnApplyChosen", { name: chosen })
                  : t("operator.btnChooseName")}
            </Button>
          </div>
        )}
      </Panel>

      <ArchiveAccountSection
        archiveFn={archiveFn}
        onArchived={async () => {
          await supabase.auth.signOut();
          await navigate({ to: "/auth" });
        }}
      />
    </ScreenContainer>
  );
}

function ArchiveAccountSection({
  archiveFn,
  onArchived,
}: {
  archiveFn: () => Promise<unknown>;
  onArchived: () => Promise<void>;
}) {
  const { t } = useTranslation("game");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const canArchive = confirmText.trim().toUpperCase() === "ARCHIVIEREN";

  const onSubmit = async () => {
    if (!canArchive || busy) return;
    setBusy(true);
    try {
      await archiveFn();
      toast.success(t("operator.archive.toastSuccess"));
      await onArchived();
    } catch (e) {
      toast.error(tServerError(e));
      setBusy(false);
    }
  };

  return (
    // tone="enemy" = Ink-Outline in Destructive: Die Farbe trägt hier die
    // Bedeutung („das hier tut weh"), sie ist keine Dekoration.
    <Panel tone="enemy" className="mt-3.5" contentClassName="p-3.5">
      <h3 className="flex items-center gap-2 text-[18px] leading-tight text-destructive">
        <ShieldAlert className="h-4 w-4 shrink-0" strokeWidth={2.5} />
        {t("operator.archive.title")}
      </h3>
      <p className="mt-1.5 text-[13px] text-muted-foreground">
        {t("operator.archive.descriptionPre")}{" "}
        <strong className="font-extrabold text-foreground">
          {t("operator.archive.descriptionBold")}
        </strong>{" "}
        {t("operator.archive.descriptionPost")}
      </p>
      {/* Wortlaut bewusst scharf (Stefan, 05.09.2026) — nur die Optik ändert
          sich, der Text nicht. */}
      <p className="mt-2 rounded-md border-2 border-destructive bg-destructive/20 px-3 py-2 text-[13px] text-foreground">
        <strong className="font-extrabold">{t("operator.archive.irreversible")}</strong>
      </p>
      <p className="mt-2 text-xs text-muted-foreground">{t("operator.archive.gdprNote")}</p>

      <div className="mt-3.5 space-y-2">
        <label className="block text-xs text-muted-foreground" htmlFor="archive-confirm">
          {t("operator.archive.confirmLabelPre")}{" "}
          <span className="font-display tracking-[0.1em] text-foreground">ARCHIVIEREN</span>{" "}
          {t("operator.archive.confirmLabelPost")}
        </label>
        <Input
          id="archive-confirm"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="ARCHIVIEREN"
          className="font-display tracking-[0.08em]"
        />
        <Button
          variant="destructive"
          disabled={!canArchive || busy}
          onClick={onSubmit}
          className="w-full sm:w-auto"
        >
          {busy ? t("operator.archive.btnBusy") : t("operator.archive.btnConfirm")}
        </Button>
      </div>
    </Panel>
  );
}
